import { Box, FormControlLabel, Grid, MenuItem, Paper, Skeleton, Switch, TextField, Typography, useTheme } from "@mui/material";
import { useCurrentWorkspace } from "src/context/reducers/app-settings";
import { useGetBreakdownChartDataQuery } from "src/context/api/conversionsAnalysis/api";
import { useDispatch, useSelector } from "react-redux";
import { useEffect, useState } from "react";
import { setFilterConversionType } from "src/context/reducers/filter-settings/filterSlice";
import ChartDonutConversions from "src/sections/charts/chart-donut-conversions";
import { CONVERSION_CHANNEL_COLORS } from "src/sections/charts/conversion-channel-colors";
import FunnelConversionsChart from './funnel/FunnelConversionsChart';
import _ from "lodash";
import Iconify from "src/components/iconify";
import { fDate } from "src/utils/format-time";

export default function ConversionsAnalysis() {
    const workspace: any = useCurrentWorkspace();
    const filterStore = useSelector((state: any) => state.filters);
    const dispatch = useDispatch();
    const theme = useTheme();
    const [metric, setMetric]: any = useState("Revenue");
    const [dimension] = useState<'channel' | 'country' | 'device' | 'platform'>('channel');
    const [conversionTypeOptions, setConversionTypeOptions] = useState<string[]>([]);
    const [conversionType, setConversionType] = useState<string>('');
    const [showLabels, setShowLabels] = useState(false);


    const MetricSelector = ({ workspaceTemplate, value, onChange }: any) => {
        const isEcommerce = workspaceTemplate === "E-commerce";

        useEffect(() => {
            if (!isEcommerce && value !== "Conversions") {
                onChange("Conversions");
            }
        }, [workspaceTemplate]);

        if (!isEcommerce) return null;

        return (
            <TextField
                select
                size="small"
                variant="outlined"
                sx={{ width: 150 }}
                value={value}
                onChange={(e) => onChange(e.target.value)}
            >
                <MenuItem value="Revenue">Revenue</MenuItem>
                <MenuItem value="Conversions">Conversions</MenuItem>
            </TextField>
        );
    }

    const { data: breakdownData, isFetching: isFetchingBreakdown, error: breakdownError } = useGetBreakdownChartDataQuery(
        {
            workspaceId: workspace?.id,
            start_date: filterStore?.start,
            end_date: filterStore?.end,
            conversionType: filterStore?.conversionType,
            metric,
            dimension,
            timestep: filterStore?.timestep,
            devices: filterStore?.devices,
            countries: filterStore?.countries,
            platform: filterStore?.platforms,
        },
        {
            skip: !(
                workspace?.id &&
                filterStore?.start &&
                filterStore?.end &&
                filterStore?.conversionType
            ),
        }
    );


    const rawAreaData: any = breakdownData?.area || [];

    const categories: any = Array.from(
        new Set(rawAreaData.map((d: any) => d.date))
    );

    const allDates = rawAreaData.map((d: any) => {
        return fDate(d?.date, 'yyyy-MM-dd');
    });

    const uniqueDate: string[] = Array.from(new Set(allDates));

    const grouped = _.groupBy(rawAreaData, "label");

    const series = Object.entries(grouped).map(([label, values]: [string, any]) => {
        const dataPerDate = _.keyBy(values, "date");
        return {
            name: label,
            data: categories.map((date: any) => dataPerDate[date]?.value || 0)
        };
    });

    // Map bar colors by series name so Organic stays green / Paid dark yellow regardless
    // of group order (Figma rebranding). Other breakdowns keep the default palette.
    const barColors = dimension === 'channel'
        ? series.map((s) => CONVERSION_CHANNEL_COLORS[s.name] || '#919eab')
        : undefined;

    useEffect(() => {
        if (workspace?.template === "Lead") {
            setMetric("Conversions");
        }
    }, [workspace])

    useEffect(() => {
        if (!workspace) return;

        let options: string[] = [];
        let defaultOption = '';

        if (workspace.custom === true) {
            options = ["Brand (Custom source)", "Brand (Ad-based)"];
            defaultOption = "Brand (Custom source)";
        } else if (workspace.conversions_google_analytics && workspace.conversions_google_analytics.length > 0) {
            options = ["Total", "Brand (Ad-based)", "Brand (Tracking-based)"];
            defaultOption = "Total";
        } else {
            options = ["Brand (Ad-based)"];
            defaultOption = "Brand (Ad-based)";
        }

        setConversionTypeOptions(options);
        setConversionType(defaultOption);
        dispatch(setFilterConversionType(defaultOption));
    }, [workspace]);

    return (
        <Paper sx={{
            backgroundColor: 'common.white',
            boxShadow: '0px 12px 24px -4px rgba(145, 158, 171, 0.12)',
            border: '1px solid #eaeaea',
            height: '450px',
            borderRadius: '16px',
            marginTop: 2,
            marginBottom: 1,
        }}>
            {isFetchingBreakdown || (!breakdownData && !breakdownError)
                ? <Skeleton width={'100%'} height={'100%'} style={{ borderRadius: 10 }} variant="rectangular" animation={"wave"}>
                    <>
                        <Iconify py={"auto"} icon={"eva:checkmark-circle-2-fill"} color={"success.main"} />
                        <Typography color={"success.main"}>Active</Typography>
                    </>
                </Skeleton>
                :
                <>
                    {/* 24px side padding: the header controls end 24px from the card edge,
                        aligned with the chart legends below. */}
                    <div style={{ padding: '16px 24px 0' }}>
                        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
                            <div>
                                <Typography variant="overline" sx={{ color: '#637381', display: 'block', lineHeight: 1.4 }}>Conversions</Typography>
                                <Typography variant="h6" sx={{ mb: 0 }}>
                                    {filterStore?.conversionType} Search {metric} By {dimension.charAt(0).toUpperCase() + dimension.slice(1)}
                                </Typography>
                            </div>

                            {/* gap plutôt que des marges droites : le dernier contrôle affleure le
                                padding du header, quels que soient les contrôles affichés. */}
                            <Box sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center', gap: 2 }}>
                                <FormControlLabel
                                    control={
                                        <Switch
                                            size="small"
                                            checked={showLabels}
                                            onChange={(event) => setShowLabels(event.target.checked)}
                                            sx={{
                                              '& .MuiSwitch-switchBase.Mui-checked': { color: '#fff' },
                                              '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: '#212B36', opacity: 1 },
                                            }}
                                        />
                                    }
                                    label="Show labels"
                                    sx={{ m: 0, '& .MuiFormControlLabel-label': { fontSize: 13, color: '#637381' } }}
                                />
                                <TextField
                                    select
                                    size="small"
                                    variant="outlined"
                                    sx={{ width: 190 }}
                                    value={conversionType}
                                    onChange={(event) => {
                                        setConversionType(event.target.value);
                                        dispatch(setFilterConversionType(event.target.value));
                                    }}
                                >
                                    {conversionTypeOptions.map((option) => (
                                        <MenuItem key={option} value={option}>
                                            {option}
                                        </MenuItem>
                                    ))}
                                </TextField>

                                {workspace?.template === "E-commerce" &&
                                    <MetricSelector
                                        workspaceTemplate={workspace?.template}
                                        value={metric}
                                        onChange={setMetric}
                                    />
                                }

                            </Box>
                        </div>
                    </div>

                    <Grid container>
                        <Grid item xs={4}>
                            <Box>
                                <ChartDonutConversions
                                    series={breakdownData?.donut?.map((item: any) => item.value) || []}
                                    labels={breakdownData?.donut?.map((item: any) => item.label) || []}
                                    breakdownDimension={dimension}
                                />
                            </Box>
                        </Grid>
                        {/* pr: 3 → la légende du chart (alignée à droite) se place à 24px du bord
                            de la carte, alignée avec les contrôles du header. */}
                        <Grid item xs={8} pl={2} pr={3}>
                            <Box>
                                <FunnelConversionsChart series={series} labels={uniqueDate} showLabels={showLabels} colors={barColors} currency={metric === "Revenue"} />
                            </Box>
                        </Grid>
                    </Grid>
                </>
            }
        </Paper>
    );
}